import React from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  SvgIcon,
  Card,
  Chip,
  CircularProgress
} from '@material-ui/core';
import { Search as SearchIcon } from 'react-feather';
import { useDispatch } from 'react-redux';
import {
  getListDevice,
  updateDeviceTransfer
} from 'src/features/deviceSlice';
import {
  syncOrderDeviceTypes,
  updateOrderStatus,
  updateOrderInList
} from 'src/features/orderSlice';
import { showGlobalToast } from 'src/features/uiSlice';
import { messageToastType_const } from 'src/app/constant/config';
import axiosInterceptor from 'src/app/axiosInterceptor';

export const parseSerialsInput = (rawInput) => {
  if (!rawInput || typeof rawInput !== 'string') return [];

  const tokens = rawInput
    .split(/[\s;\r\n]+/)
    .map(s => s.trim())
    .filter(Boolean);

  const result = [];

  for (const token of tokens) {
    if (token.includes(',')) {
      const parts = token.split(',').map(s => s.trim()).filter(Boolean);
      if (parts.length >= 2) {
        const firstPart = parts[0];
        const lastPart = parts[parts.length - 1];

        const isQRFormat =
          firstPart.toUpperCase().startsWith('VGPS') ||
          parts.some(p => /^\d+\.\d+$/.test(p)) ||
          (parts.length >= 3 && isNaN(Number(firstPart)));

        if (isQRFormat) {
          if (lastPart) result.push(lastPart);
        } else {
          parts.forEach(p => {
            if (p) result.push(p);
          });
        }
      } else if (parts.length === 1) {
        result.push(parts[0]);
      }
    } else {
      result.push(token);
    }
  }

  const finalSerials = [];
  for (let i = 0; i < result.length; i++) {
    const item = result[i];
    const isModelOrVer =
      item.toUpperCase().startsWith('VGPS') ||
      /^\d+\.\d+$/.test(item) ||
      (isNaN(Number(item)) && item.length < 15);

    if (!isModelOrVer) {
      finalSerials.push(item);
    }
  }

  return Array.from(new Set(finalSerials));
};

const SerialDialog = ({
  editingOrder,
  setEditingOrder,
  newSerial,
  setNewSerial,
  editingDevices,
  setEditingDevices,
  isAdmin,
  isAccountant,
  isSale,
  isLeadMarketing,
  isAgency,
  fetchListOrders,
  fetchOrderReceipts,
  fetchAgencyDebts,
  listDeviceType,
  handleBypassSerial
}) => {
  const dispatch = useDispatch();
  const loadingSerialsRef = React.useRef(new Set());
  const [loadingOrder, setLoadingOrder] = React.useState(false);

  React.useEffect(() => {
    if (!editingOrder?.id) return;

    const hasMissingIds =
      !editingDevices ||
      editingDevices.length === 0 ||
      editingDevices.some(d => !d.id);

    if (hasMissingIds) {
      setLoadingOrder(true);
      axiosInterceptor
        .get(`/order/${editingOrder.id}`)
        .then(res => {
          const full = res.data?.payload || res.data;
          if (full && full.id) {
            setEditingOrder(prev => ({ ...(prev || {}), ...full }));
            const serverDevList = (
              full.order_device_types ||
              full.devices ||
              []
            ).map(odt => ({
              ...JSON.parse(JSON.stringify(odt)),
              device_type_name:
                odt.device_type?.name ||
                odt.device_type_name ||
                odt.type ||
                listDeviceType?.find(
                  lt => lt.id === (odt.device_type_id || odt.device_type?.id)
                )?.name ||
                'N/A',
              device_ids:
                odt.order_details?.map(od => od.device_id).filter(Boolean) ||
                odt.device_ids ||
                [],
              serials:
                odt.order_details?.map(
                  od => od.serial || od.device?.serial || od.device?.imei
                ).filter(Boolean) ||
                odt.serials ||
                []
            }));
            if (serverDevList.length > 0) {
              setEditingDevices(serverDevList);
            }
          }
        })
        .catch(err => {
          console.error('Fetch order detail failed in SerialDialog:', err);
        })
        .finally(() => {
          setLoadingOrder(false);
        });
    }
  }, [editingOrder?.id]);

  const formatErrorWithSerials = (msg, devicesList) => {
    let newMsg = msg;
    const idRegex = /ID (\d+)/g;
    const matches = msg.matchAll(idRegex);

    [...matches].forEach(match => {
      const idStr = match[0];
      const idNum = parseInt(match[1]);
      let serial = null;
      devicesList.forEach(d => {
        const foundIdx = (d.device_ids || []).indexOf(idNum);
        if (foundIdx !== -1) {
          serial = (d.serials || [])[foundIdx];
        }
      });
      if (serial) {
        newMsg = newMsg.replace(idStr, serial);
      }
    });

    return newMsg
      .replace(/các ID:/g, 'các Serial:')
      .replace(/các ID :/g, 'các Serial:')
      .replace(/ID:/g, 'Serial:')
      .replace(/ID :/g, 'Serial :')
      .replace(/ID /g, 'Serial ');
  };

  const handleSaveSerials = async () => {
    if (editingDevices.some(d => !d.id)) {
      dispatch(
        showGlobalToast({
          message: 'Đang làm mới thông tin thiết bị, vui lòng thử lại sau giây lát!',
          type: messageToastType_const.warning
        })
      );
      try {
        setLoadingOrder(true);
        const res = await axiosInterceptor.get(`/order/${editingOrder.id}`);
        const full = res.data?.payload || res.data;
        if (full && full.id) {
          setEditingOrder(prev => ({ ...(prev || {}), ...full }));
          const serverDevList = (
            full.order_device_types ||
            full.devices ||
            []
          ).map((odt, idx) => {
            const currentDev = editingDevices.find(
              d => d.id === odt.id || d.device_type_id === (odt.device_type_id || odt.device_type?.id)
            ) || editingDevices[idx];
            return {
              ...odt,
              device_type_name:
                odt.device_type?.name ||
                odt.device_type_name ||
                odt.type ||
                listDeviceType?.find(
                  lt => lt.id === (odt.device_type_id || odt.device_type?.id)
                )?.name ||
                'N/A',
              device_ids:
                currentDev?.device_ids ||
                odt.order_details?.map(od => od.device_id).filter(Boolean) ||
                [],
              serials:
                currentDev?.serials ||
                odt.order_details?.map(
                  od => od.serial || od.device?.serial || od.device?.imei
                ).filter(Boolean) ||
                []
            };
          });
          setEditingDevices(serverDevList);
        }
      } catch (err) {
        console.error('Refresh order failed:', err);
      } finally {
        setLoadingOrder(false);
      }
      return;
    }

    const isMissing = editingDevices.some(
      d => (d.serials?.length || 0) < d.quantity
    );
    if (isMissing && !(isAdmin || isAccountant)) {
      dispatch(
        showGlobalToast({
          message: 'Vui lòng quét đủ tất cả serial cho thiết bị trước khi lưu!',
          type: messageToastType_const.warning
        })
      );
      return;
    }

    const payload = {
      items: editingDevices.map(d => ({
        order_device_type_id: d.id,
        device_ids: d.device_ids || []
      }))
    };

    dispatch(
      syncOrderDeviceTypes({
        id: editingOrder.id,
        data: payload,
        suffix: '/order_device_type'
      })
    ).then(async res => {
      if (res.meta.requestStatus === 'fulfilled') {
        if ((editingOrder.status || 0) < 1) {
          const isOffline =
            editingOrder.delivery_method === 0 ||
            editingOrder.installer === 'VGPS lắp đặt';
          const targetStatus = isOffline ? 2 : 1;
          const targetNote = isOffline
            ? 'Sẵn sàng giao (Offline)'
            : 'Đang xử lý';
          dispatch(
            updateOrderStatus({
              id: editingOrder.id,
              data: { status: targetStatus, note: targetNote },
              suffix: '/status'
            })
          ).then(statusRes => {
            if (statusRes.meta.requestStatus === 'fulfilled') {
              dispatch(
                updateOrderInList({
                  id: editingOrder.id,
                  data: { status: targetStatus }
                })
              );
              if (fetchListOrders) fetchListOrders();
              if (fetchOrderReceipts) fetchOrderReceipts();
              if (fetchAgencyDebts && !isSale && !isLeadMarketing) fetchAgencyDebts();
            }
            setEditingOrder(null);
          });
        } else {
          if (fetchListOrders) fetchListOrders();
          if (fetchOrderReceipts) fetchOrderReceipts();
          if (fetchAgencyDebts && !isSale && !isLeadMarketing) fetchAgencyDebts();
          setEditingOrder(null);
        }
      } else {
        const errorMsg = res.payload?.message || res.error?.message || '';
        let displayMsg =
          errorMsg || 'Lưu Serial thất bại. Vui lòng kiểm tra lại!';

        if (errorMsg.includes('Device type mismatch')) {
          displayMsg = 'Serial sai loại thiết bị!';
        } else if (
          errorMsg.includes('Thiết bị đã được dùng') ||
          errorMsg.includes('tồn tại trong đơn hàng') ||
          errorMsg.includes('already') ||
          errorMsg.includes('ID ')
        ) {
          // Tự động tìm xem serial nào bị trùng và đang thuộc về đơn hàng nào
          const allSerials = editingDevices.reduce(
            (acc, d) => [...acc, ...(d.serials || [])],
            []
          );
          let foundInfo = null;

          for (const sn of allSerials) {
            try {
              const checkRes = await axiosInterceptor.get(
                `/order?keywords=${encodeURIComponent(sn)}`
              );
              const orders =
                checkRes.data?.payload?.orders ||
                checkRes.data?.orders ||
                (Array.isArray(checkRes.data?.payload)
                  ? checkRes.data.payload
                  : []);
              const existingOrder = orders.find(
                o => String(o.id) !== String(editingOrder.id)
              );
              if (existingOrder) {
                const cName =
                  existingOrder.end_user?.full_name ||
                  existingOrder.agency?.name ||
                  existingOrder.customer_name ||
                  'N/A';
                const sttName =
                  existingOrder.status === 3 ? 'Đã giao hàng' : 'Đang xử lý';
                foundInfo = `Serial '${sn}' đang ở Đơn hàng #${existingOrder.code || existingOrder.id} (${cName} - ${sttName}). Vui lòng gỡ Serial này ở đơn #${existingOrder.code || existingOrder.id} trước!`;
                break;
              }
            } catch (err) {
              console.error('Lookup duplicate order failed:', err);
            }
          }

          displayMsg =
            foundInfo || formatErrorWithSerials(displayMsg, editingDevices);
        } else {
          displayMsg = formatErrorWithSerials(displayMsg, editingDevices);
        }

        dispatch(
          showGlobalToast({
            message: displayMsg,
            type: messageToastType_const.error
          })
        );
      }
    });
  };

  const canEditSerial =
    !isAgency &&
    editingOrder?.status !== -1 &&
    (editingOrder?.status < 3 || isAdmin || isAccountant);

  return (
    <Dialog
      open={Boolean(editingOrder)}
      onClose={() => setEditingOrder(null)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle disableTypography style={{ padding: '16px 24px' }}>
        <Box>
          <Typography
            variant="h5"
            style={{ fontWeight: 700, color: '#1a1f36' }}
          >
            Serial thiết bị
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {editingOrder?.customer_name || editingOrder?.customerName}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers style={{ backgroundColor: '#fcfcfd' }}>
        {loadingOrder && (
          <Box display="flex" alignItems="center" justifyContent="center" py={1} mb={2}>
            <CircularProgress size={20} />
            <Typography variant="body2" style={{ marginLeft: 8, color: '#666', fontSize: 13 }}>
              Đang đồng bộ dữ liệu thiết bị...
            </Typography>
          </Box>
        )}
        {canEditSerial && (
          <Box mb={3} mt={1}>
            <TextField
              fullWidth
              autoFocus
              label="Bắn QR Code / Quét Serial tại đây..."
              placeholder="Nhấn Enter để thêm..."
              value={newSerial}
              onChange={e => setNewSerial(e.target.value)}
              onKeyDown={async e => {
                if (e.key === 'Enter' && newSerial.trim()) {
                  const rawInput = newSerial.trim();
                  setNewSerial('');

                  const snList = parseSerialsInput(rawInput);

                  if (snList.length === 0) return;

                  // Filter out serials that are already added or currently being processed
                  const serialsToProcess = snList.filter(sn => {
                    const alreadyInDevices = editingDevices.some(d => d.serials?.includes(sn));
                    const isLoading = loadingSerialsRef.current.has(sn);
                    return !alreadyInDevices && !isLoading;
                  });

                  if (serialsToProcess.length === 0) {
                    dispatch(
                      showGlobalToast({
                        message: 'Tất cả serial nhập vào đã tồn tại trong đơn hoặc đang được xử lý!',
                        type: messageToastType_const.warning
                      })
                    );
                    return;
                  }

                  // Check total available slot capacity across all device types
                  const totalAvailableSlots = editingDevices.reduce(
                    (acc, d) => acc + Math.max(0, d.quantity - (d.serials?.length || 0)),
                    0
                  );

                  if (totalAvailableSlots === 0) {
                    dispatch(
                      showGlobalToast({
                        message: 'Đã đủ số lượng serial cho tất cả thiết bị!',
                        type: messageToastType_const.info
                      })
                    );
                    return;
                  }

                  const finalSerials = serialsToProcess.slice(0, totalAvailableSlots);
                  const excessCount = serialsToProcess.length - finalSerials.length;

                  finalSerials.forEach(sn => loadingSerialsRef.current.add(sn));

                  try {
                    const results = await Promise.all(
                      finalSerials.map(sn =>
                        dispatch(
                          getListDevice({
                            keywords: sn,
                            page: 1,
                            page_size: 1000
                          })
                        )
                          .then(res => {
                            const devices =
                              res.payload?.payload?.devices ||
                              res.payload?.devices ||
                              [];
                            const foundDevice = devices.find(
                              d =>
                                String(d.serial || '').trim().toLowerCase() === sn.toLowerCase() ||
                                String(d.imei || '').trim().toLowerCase() === sn.toLowerCase()
                            );
                            return { sn, device: foundDevice, devicesFoundCount: devices.length };
                          })
                          .catch(() => ({ sn, device: null, devicesFoundCount: 0 }))
                          .finally(() => {
                            loadingSerialsRef.current.delete(sn);
                          })
                      )
                    );

                    setEditingDevices(prevEditingDevices => {
                      const newEditingDevices = JSON.parse(JSON.stringify(prevEditingDevices));
                      let successCount = 0;
                      const notFoundSerials = [];
                      const mismatchSerials = [];

                      for (const { sn, device } of results) {
                        if (!device) {
                          notFoundSerials.push(sn);
                          continue;
                        }

                        const apiDeviceTypeName =
                          device.deviceType?.name ||
                          device.device_type?.name ||
                          null;

                        let targetIdx = -1;
                        if (apiDeviceTypeName) {
                          targetIdx = newEditingDevices.findIndex(
                            d =>
                              (
                                d.device_type_name ||
                                d.type ||
                                ''
                              ).toLowerCase() ===
                                apiDeviceTypeName.toLowerCase() &&
                              (d.serials?.length || 0) < d.quantity
                          );
                        }

                        if (targetIdx === -1) {
                          mismatchSerials.push({ sn, type: apiDeviceTypeName });
                          continue;
                        }

                        const targetDevice = newEditingDevices[targetIdx];
                        targetDevice.device_ids = targetDevice.device_ids || [];
                        targetDevice.serials = targetDevice.serials || [];

                        if (!targetDevice.serials.includes(sn)) {
                          targetDevice.device_ids.push(device.id);
                          targetDevice.serials.push(sn);
                          successCount++;
                        }
                      }

                      if (successCount > 0) {
                        const excessMsg = excessCount > 0 ? ` (Đã bỏ qua ${excessCount} serial thừa)` : '';
                        dispatch(
                          showGlobalToast({
                            message: `Đã thêm thành công ${successCount} serial!${excessMsg}`,
                            type: messageToastType_const.success
                          })
                        );
                      }

                      if (notFoundSerials.length > 0) {
                        dispatch(
                          showGlobalToast({
                            message: `Không tìm thấy ${notFoundSerials.length} serial trong kho: ${notFoundSerials.join(', ')}`,
                            type: messageToastType_const.error
                          })
                        );
                      }

                      if (mismatchSerials.length > 0) {
                        const detailStr = mismatchSerials.map(m => `'${m.sn}' (${m.type || 'không rõ'})`).join(', ');
                        dispatch(
                          showGlobalToast({
                            message: `${mismatchSerials.length} serial không khớp loại thiết bị hoặc đã đủ số lượng: ${detailStr}`,
                            type: messageToastType_const.warning
                          })
                        );
                      }

                      return newEditingDevices;
                    });
                  } catch (err) {
                    console.error('Error adding batch serials:', err);
                  }
                }
              }}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
                style: {
                  backgroundColor: 'white',
                  padding: '0 4px',
                  marginLeft: -4
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon fontSize="small" color="action">
                      <SearchIcon />
                    </SvgIcon>
                  </InputAdornment>
                )
              }}
            />
          </Box>
        )}

        <Box display="flex" flexDirection="column" style={{ gap: 20 }}>
          {editingDevices.map((device, idx) => (
            <Card
              key={idx}
              variant="outlined"
              style={{
                padding: 16,
                borderRadius: 12,
                border: '1px solid #e0e3ef'
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1.5}
              >
                <Typography
                  style={{
                    fontWeight: 700,
                    color: '#1a73e8',
                    fontSize: 14
                  }}
                >
                  {device.device_type_name || device.type}
                </Typography>
                <Chip
                  label={`${device.serials?.length || 0} / ${
                    device.quantity
                  }`}
                  color={
                    device.serials?.length === device.quantity
                      ? 'primary'
                      : 'default'
                  }
                  size="small"
                  style={{ fontWeight: 700 }}
                />
              </Box>
              <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                {device.serials?.map((sn, sIdx) => (
                  <Chip
                    key={sIdx}
                    label={sn}
                    onDelete={
                      canEditSerial
                        ? () => {
                            const newEditingDevices = [...editingDevices];
                            newEditingDevices[idx] = {
                              ...device,
                              serials: device.serials.filter(
                                (_, i) => i !== sIdx
                              ),
                              device_ids: (device.device_ids || []).filter(
                                (_, i) => i !== sIdx
                              )
                            };
                            setEditingDevices(newEditingDevices);
                          }
                        : undefined
                    }
                    variant="outlined"
                    size="small"
                    style={{
                      background: 'white',
                      borderColor: '#1a73e840',
                      fontWeight: 500
                    }}
                  />
                ))}
              </Box>
            </Card>
          ))}
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px' }}>
        <Button
          onClick={() => setEditingOrder(null)}
          color="default"
          style={{ textTransform: 'none', fontWeight: 600 }}
        >
          Đóng
        </Button>
        {!isAgency &&
          editingOrder?.status < 3 &&
          editingOrder?.status !== -1 &&
          Boolean(editingOrder.agency_id || editingOrder.agency?.id) && (
            <Button
              variant="contained"
              color="secondary"
              disabled={loadingOrder}
              onClick={() => {
                const list_device_id = editingDevices.reduce(
                  (acc, d) => [...acc, ...(d.device_ids || [])],
                  []
                );
                if (list_device_id.length === 0) {
                  dispatch(
                    showGlobalToast({
                      message: 'Vui lòng quét đủ serial trước khi chuyển kho!',
                      type: messageToastType_const.warning
                    })
                  );
                  return;
                }
                const agencyId =
                  editingOrder.agency_id || editingOrder.agency?.id;
                dispatch(
                  updateDeviceTransfer({
                    agency_id: agencyId,
                    list_device_id: list_device_id
                  })
                ).then(res => {
                  if (res.meta.requestStatus === 'fulfilled') {
                    if (fetchListOrders) fetchListOrders();
                    if (fetchOrderReceipts) fetchOrderReceipts();
                    if (fetchAgencyDebts && !isSale && !isLeadMarketing) fetchAgencyDebts();
                  }
                });
              }}
              style={{
                textTransform: 'none',
                fontWeight: 600,
                padding: '6px 24px'
              }}
            >
              Chuyển kho
            </Button>
          )}
        {canEditSerial && (
          <Button
            variant="contained"
            color="primary"
            disabled={loadingOrder}
            onClick={handleSaveSerials}
            style={{
              textTransform: 'none',
              fontWeight: 600,
              padding: '6px 24px'
            }}
          >
            Lưu serial
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SerialDialog;
